export const TRAINER_DECLARATION_TYPE = 'Trainer/Assessor Declaration';
export const STUDENT_DECLARATION_TYPE = 'Student Declaration';
export const getTrafficPostEoiPath = (result, trainingType) => result?.flowId
  ? `/trafficCourseFlow?sid=${encodeURIComponent(result.flowId)}&trainingType=${trainingType}` : '/thankyou';
export const getTrafficDeclarationPath = (flow = {}) => {
  const type = flow.linkedCourse === 'traffic-controller' ? 'trafficControl'
    : flow.linkedCourse === 'traffic-implementer' ? 'trafficImplementation' : flow.trainingType;
  if (!flow.id || !['trafficControl', 'trafficImplementation'].includes(type)) return '';
  const params = new URLSearchParams({ sid: flow.id });
  if (flow.trainerId) params.set('trainerId', flow.trainerId);
  if (flow.delivery) params.set('delivery', flow.delivery);
  return `/dec-${type}?${params.toString()}`;
};
export const STUDENT_VISIBLE_TASK_TYPES = new Set([
  'Student instructions',
  'Conditions of assessment',
  'Theory Questions',
  'Activity',
  STUDENT_DECLARATION_TYPE,
]);

export const isStudentVisibleTask = (task = {}) => task.type !== TRAINER_DECLARATION_TYPE;
export const isTrafficIntroduction = task => ['Student instructions', 'Conditions of assessment'].includes(task.type);

export const isTrafficTaskAccessible = (flow, task) => {
  const state = getTaskState(flow, task.id);
  if (!state || state.status === 'locked' || state.priorCompletion) return false;
  if (task.type === 'Theory Questions') return (flow.tasks || []).filter(isTrafficIntroduction)
    .every(item => ['submitted', 'completed'].includes(getTaskState(flow, item.id)?.status));
  if (task.type === 'Conditions of assessment') return (flow.tasks || []).filter(item => item.type === 'Student instructions')
    .every(item => ['submitted', 'completed'].includes(getTaskState(flow, item.id)?.status));
  return true;
};

export const hasAutomaticAnswerKey = (question = {}) => {
  const answer = question.acceptedAnswer ?? question.correctAnswer ?? question.answerGuide;
  return Array.isArray(answer) ? answer.length > 0 : String(answer ?? '').trim() !== '';
};

const isAutoMarkedTheoryPrompt = (question = {}, forceFillBlank = false) => {
  const type = question.responseType || question.type;
  const wordBankFillBlank = type === 'fill-blank'
    || (Array.isArray(question.subQuestions) && question.subQuestions.length > 0 && /word\s*bank/i.test(String(question.text || '')));
  const displayAsFillBlank = forceFillBlank || type === 'fill-blank' || wordBankFillBlank;

  if (question.markingMode === 'trainer') {
    return false;
  }

  if (displayAsFillBlank) {
    return true;
  }

  if (['multiple-choice', 'image-multiple-choice', 'true-false', 'yes-no', 'multiple-response', 'numeric', 'matching', 'ordering', 'name-following'].includes(type)) {
    return true;
  }

  if (['short-answer', 'diagram-questions'].includes(type) && hasAutomaticAnswerKey(question)) {
    return true;
  }

  return false;
};

export const getTheoryReviewTargets = (questions = []) => (
  (questions || []).flatMap((question) => {
    if (question.type === 'student-guide') {
      return [];
    }

    const wordBankFillBlank = question.type === 'fill-blank'
      || (Array.isArray(question.subQuestions) && question.subQuestions.length > 0 && /word\s*bank/i.test(String(question.text || '')));
    if (question.subQuestions?.length) {
      return question.subQuestions
        .filter((part) => !isAutoMarkedTheoryPrompt({ ...part, type: part.responseType || question.type }, wordBankFillBlank))
        .map((part, index) => ({
          id: `${question.id}::${part.id}`,
          questionId: question.id,
          partId: part.id,
          label: `${question.numberLabel || 'Question'} ${String.fromCharCode(97 + index)}`,
        }));
    }

    return isAutoMarkedTheoryPrompt(question)
      ? []
      : [{
        id: question.id,
        questionId: question.id,
        partId: '',
        label: question.numberLabel || 'Question',
      }];
  })
);

// A theory section whose questions are all automatically marked leaves the trainer
// nothing to assess. The student flow only reaches 'submitted' once every automatic
// answer is correct, so such a section is finished the moment it is submitted.
export const theoryNeedsTrainerMarking = (task = {}) => (
  task.type === 'Theory Questions' && getTheoryReviewTargets(task.questions || []).length > 0
);

export const buildTrafficTaskStateMap = (tasks = []) => {
  return tasks.reduce((acc, task) => {
    const studentVisible = isStudentVisibleTask(task);
    // The EOI only makes the course available. Trainers explicitly release every
    // assessable stage, so completing one stage never grants access to the next.
    const status = !studentVisible ? 'trainer_only' : 'locked';
    acc[task.id] = {
      taskId: task.id,
      type: task.type || '',
      name: task.name || '',
      studentVisible,
      status,
      submittedAt: '',
      completedAt: '',
    };
    return acc;
  }, {});
};

export const getOrderedStudentTasks = (flowDoc = {}) => (
  (flowDoc.tasks || []).filter(isStudentVisibleTask)
);

export const getTaskState = (flowDoc = {}, taskId = '') => flowDoc.taskStates?.[taskId] || null;

export const getCurrentAvailableTaskId = (flowDoc = {}) => {
  const orderedTasks = getOrderedStudentTasks(flowDoc);
  const availableTask = orderedTasks.find((task) => {
    const state = getTaskState(flowDoc, task.id);
    return state?.status === 'available' && isTrafficTaskAccessible(flowDoc, task);
  });
  return availableTask?.id || '';
};

export const isTaskSubmitted = (flowDoc = {}, taskId = '') => {
  const status = getTaskState(flowDoc, taskId)?.status;
  return status === 'submitted' || status === 'completed';
};

export const buildNextTrafficTaskStates = (flowDoc = {}, completedTaskId = '') => {
  const nextStates = { ...(flowDoc.taskStates || {}) };
  const orderedTasks = getOrderedStudentTasks(flowDoc);
  const now = new Date().toISOString();

  if (nextStates[completedTaskId]) {
    nextStates[completedTaskId] = {
      ...nextStates[completedTaskId],
      status: 'submitted',
      returnedForCorrection: false,
      submittedAt: nextStates[completedTaskId].submittedAt || now,
      completedAt: nextStates[completedTaskId].completedAt || now,
    };
  }

  const allStudentTasksCompleted = orderedTasks.every((task) => ['submitted', 'completed'].includes(nextStates[task.id]?.status));
  const trainerDeclarationTask = (flowDoc.tasks || []).find((task) => task.type === TRAINER_DECLARATION_TYPE);

  if (trainerDeclarationTask && nextStates[trainerDeclarationTask.id]) {
    nextStates[trainerDeclarationTask.id] = {
      ...nextStates[trainerDeclarationTask.id],
      status: allStudentTasksCompleted
        ? (nextStates[trainerDeclarationTask.id].status === 'completed' ? 'completed' : 'available')
        : 'trainer_only',
    };
  }

  return nextStates;
};

export const getFlowTaskById = (flowDoc = {}, taskId = '') => (
  (flowDoc.tasks || []).find((task) => task.id === taskId) || null
);

export const getTrafficStageStatusLabel = (state = {}) => {
  if (state?.status === 'completed' && state.priorCompletion) return 'Completed - credited';
  if (state?.returnedForCorrection && state.status === 'available') return 'Reattempt Required';
  switch (state?.status) {
    case 'submitted':
      return 'Submitted';
    case 'completed':
      return 'Completed';
    case 'available':
      return 'In Progress';
    case 'trainer_only':
      return 'Trainer';
    case 'locked':
      return 'Locked';
    default:
      return 'Pending';
  }
};

export const buildTrafficReviewState = (flow, task, review) => {
  const failedKeys = Object.entries(task.type === 'Activity' ? review.checklistMarks || {} : review.questionMarks || {})
    .filter(([, mark]) => mark === 'NYC').map(([key]) => key);
  const questionIds = (task.questions || []).filter(q => failedKeys.some(key => key.split('::')[0] === String(q.id))).map(q => q.id);
  const states = { ...flow.taskStates };
  if (failedKeys.length) {
    states[task.id] = { ...states[task.id], status: 'available', returnedForCorrection: true, completedAt: '' };
    const trainer = (flow.tasks || []).find(row => row.type === TRAINER_DECLARATION_TYPE);
    if (trainer) states[trainer.id] = { ...states[trainer.id], status: 'trainer_only', completedAt: '' };
  }
  return { states, questionIds };
};

export const clearReturnedTrafficMarks = response => ({
  ...response,
  assessorRetryQuestionIds: [],
  previousAssessorReview: response.assessorReview || {},
  assessorReview: {
    ...(response.assessorReview || {}),
    questionMarks: Object.fromEntries(Object.entries(response.assessorReview?.questionMarks || {}).filter(([, value]) => value !== 'NYC')),
    checklistMarks: Object.fromEntries(Object.entries(response.assessorReview?.checklistMarks || {}).filter(([, value]) => value !== 'NYC')),
  },
});
